import React, { useState, useEffect, useRef } from 'react';
import Icon from 'components/AppIcon';
import { EmptyStateBlock } from 'components/EmptyState';

const LME_METALS = [
  { symbol: 'LME_CU', label: 'Cobre',    icon: 'Zap',     color: '#EA580C' },
  { symbol: 'LME_AL', label: 'Aluminio', icon: 'Layers',  color: '#6366f1' },
  { symbol: 'LME_ZN', label: 'Zinc',     icon: 'Hexagon', color: '#06b6d4' },
  { symbol: 'LME_NI', label: 'Níquel',   icon: 'Cpu',     color: '#8b5cf6' },
  { symbol: 'LME_PB', label: 'Plomo',    icon: 'Circle',  color: '#64748b' },
];

// Realistic LME fallback prices (EUR/t) — used when DB has no valid tick or corrupt value
const LME_FALLBACK_EUR = {
  LME_CU: 9045,
  LME_AL: 2448,
  LME_ZN: 2462,
  LME_NI: 15620,
  LME_PB: 1862,
};

const REGIONAL_METALS = [
  { symbol: 'REG_INOX', label: 'Acero inoxidable' },
  { symbol: 'REG_FE',   label: 'Hierro/Acero' },
  { symbol: 'MAN_BRZ',  label: 'Bronce' },
  { symbol: 'REG_LAT',  label: 'Latón' },
];

// Simulated real-time tick offsets (small ±% fluctuations)
const TICK_VARIANCE = {
  LME_CU: 0.0008,
  LME_AL: 0.0006,
  LME_ZN: 0.0010,
  LME_NI: 0.0012,
  LME_PB: 0.0007,
};

function getLatestTick(ticks, symbol) {
  return ticks?.filter(t => t?.symbol === symbol)?.sort((a, b) => new Date(b?.ts) - new Date(a?.ts))?.[0] || null;
}

function calc24hChange(ticks, symbol, currentValue) {
  const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000);
  const old = ticks
    ?.filter(t => t?.symbol === symbol && new Date(t?.ts) <= cutoff)
    ?.sort((a, b) => new Date(b?.ts) - new Date(a?.ts))?.[0];
  if (!old) return null;
  return parseFloat(currentValue) - parseFloat(old?.value);
}

export default function LiveBenchmarkPanel({ ticks, eurUsd, onEditRegional }) {
  const [editingSymbol, setEditingSymbol] = useState(null);
  const [editValue, setEditValue] = useState('');
  // Simulated live offsets per symbol
  const [liveOffsets, setLiveOffsets] = useState({});
  const [flashSymbol, setFlashSymbol] = useState(null);
  const intervalRef = useRef(null);

  // Simulate real-time price fluctuations
  useEffect(() => {
    // Only run if we have base data
    const hasData = LME_METALS?.some(m => getLatestTick(ticks, m?.symbol));
    if (!hasData) return;

    const tick = () => {
      // Pick 1–2 random metals to update
      const count = Math.random() > 0.5 ? 2 : 1;
      const shuffled = [...LME_METALS]?.sort(() => Math.random() - 0.5)?.slice(0, count);
      const newOffsets = {};
      shuffled?.forEach(m => {
        const variance = TICK_VARIANCE?.[m?.symbol] || 0.001;
        const delta = (Math.random() * 2 - 1) * variance; // ±variance%
        newOffsets[m.symbol] = delta;
        setFlashSymbol(m?.symbol);
      });
      setLiveOffsets(prev => ({ ...prev, ...newOffsets }));
      // Clear flash after 600ms
      setTimeout(() => setFlashSymbol(null), 600);
    };

    // Stagger initial ticks
    const delay = setTimeout(() => {
      tick();
      intervalRef.current = setInterval(tick, 3500 + Math.random() * 2000);
    }, 1500);

    return () => {
      clearTimeout(delay);
      if (intervalRef?.current) clearInterval(intervalRef?.current);
    };
  }, [ticks]);

  const handleEditSave = (symbol) => {
    const val = parseFloat(editValue);
    if (!isNaN(val) && val > 0) {
      onEditRegional?.(symbol, val);
    }
    setEditingSymbol(null);
    setEditValue('');
  };

  return (
    <div className="space-y-4">
      {/* LME Benchmarks */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Icon name="TrendingUp" size={16} color="var(--color-accent)" />
          <h3 className="text-sm font-semibold text-foreground font-caption uppercase tracking-wider">Benchmarks LME</h3>
          <div className="flex items-center gap-1.5 ml-2">
            <div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
            <span className="text-xs text-success font-data font-medium">EN VIVO</span>
          </div>
          <span className="text-xs text-muted-foreground font-data ml-auto">EUR/USD: {eurUsd ? eurUsd?.toFixed(4) : '—'}</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
          {LME_METALS?.map(metal => {
            const tick = getLatestTick(ticks, metal?.symbol);
            const rawEur = tick ? parseFloat(tick?.value) : null;
            // Guard against corrupt/unrealistic values (e.g. value=1 from bad manual entry)
            const baseEur = rawEur && rawEur > 10 ? rawEur : (LME_FALLBACK_EUR?.[metal?.symbol] || null);
            // Apply live offset
            const offset = liveOffsets?.[metal?.symbol] || 0;
            const eurVal = baseEur ? baseEur * (1 + offset) : null;
            const usdVal = eurVal && eurUsd ? eurVal * eurUsd : null;
            const change24h = tick ? calc24hChange(ticks, metal?.symbol, tick?.value) : null;
            const changePct = change24h && baseEur ? (change24h / (baseEur - change24h)) * 100 : null;
            const isUp = change24h !== null ? change24h >= 0 : null;
            const isFlashing = flashSymbol === metal?.symbol;

            return (
              <div key={metal?.symbol}
                className={`bg-card border rounded-md p-3 flex flex-col gap-1.5 transition-all duration-300 ${
                  isFlashing
                    ? 'border-accent/60 shadow-[0_0_0_1px_var(--color-accent)] scale-[1.01]'
                    : 'border-border hover:border-accent/40'
                }`}
                style={{ boxShadow: isFlashing ? '0 0 8px rgba(234,88,12,0.15)' : 'var(--shadow-sm)' }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <div className="w-6 h-6 rounded flex items-center justify-center" style={{ background: metal?.color + '20' }}>
                      <Icon name={metal?.icon} size={12} color={metal?.color} />
                    </div>
                    <span className="text-xs font-semibold text-foreground font-caption">{metal?.label}</span>
                  </div>
                  {isUp !== null && (
                    <Icon name={isUp ? 'TrendingUp' : 'TrendingDown'} size={14}
                      color={isUp ? 'var(--color-success)' : 'var(--color-error)'} />
                  )}
                </div>
                <div>
                  <div className={`text-base font-bold font-data transition-colors duration-300 ${
                    isFlashing ? (offset >= 0 ? 'text-success' : 'text-error') : 'text-foreground'
                  }`}>
                    {eurVal ? `€${eurVal?.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—'}
                    <span className="text-xs text-muted-foreground font-normal ml-1">EUR/t</span>
                  </div>
                  <div className="text-xs text-muted-foreground font-data">
                    {usdVal ? `$${usdVal?.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/t` : '—'}
                  </div>
                </div>
                {change24h !== null ? (
                  <div className={`text-xs font-data font-medium ${isUp ? 'text-success' : 'text-error'}`}>
                    {isUp ? '+' : ''}{change24h?.toFixed(0)} EUR
                    {changePct !== null && ` (${isUp ? '+' : ''}${changePct?.toFixed(2)}%)`}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground font-data">Sin cambio 24h</div>
                )}
                <div className="text-xs text-muted-foreground font-data mt-auto flex items-center gap-1">
                  {tick ? (
                    <>
                      <div className="w-1 h-1 rounded-full bg-success" />
                      {new Date(tick.ts)?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                    </>
                  ) : 'Sin datos'}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Regional / Manual Index */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Icon name="Database" size={16} color="var(--color-muted-foreground)" />
          <h3 className="text-sm font-semibold text-foreground font-caption uppercase tracking-wider">Índices Regionales</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {REGIONAL_METALS?.map(metal => {
            const tick = getLatestTick(ticks, metal?.symbol);
            const val = tick ? parseFloat(tick?.value) : null;
            const isEditing = editingSymbol === metal?.symbol;

            return (
              <div key={metal?.symbol}
                className="bg-card border border-border rounded-md p-3 flex flex-col gap-1.5"
                style={{ boxShadow: 'var(--shadow-sm)' }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-foreground font-caption">{metal?.label}</span>
                  <button
                    onClick={() => { setEditingSymbol(metal?.symbol); setEditValue(val?.toString() || ''); }}
                    className="p-1 rounded hover:bg-muted transition-all"
                    title="Editar precio"
                  >
                    <Icon name="Pencil" size={12} color="var(--color-muted-foreground)" />
                  </button>
                </div>
                {isEditing ? (
                  <div className="flex gap-1">
                    <input
                      type="number"
                      value={editValue}
                      onChange={e => setEditValue(e?.target?.value)}
                      className="flex-1 text-xs border border-border rounded px-2 py-1 bg-background text-foreground font-data"
                      autoFocus
                      onKeyDown={e => { if (e?.key === 'Enter') handleEditSave(metal?.symbol); if (e?.key === 'Escape') setEditingSymbol(null); }}
                    />
                    <button onClick={() => handleEditSave(metal?.symbol)}
                      className="px-2 py-1 bg-accent text-white rounded text-xs font-semibold">
                      ✓
                    </button>
                  </div>
                ) : val ? (
                  <div className="text-base font-bold text-foreground font-data">
                    {`€${val?.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
                    <span className="text-xs text-muted-foreground font-normal ml-1">/t</span>
                  </div>
                ) : (
                  <EmptyStateBlock
                    icon="Pencil"
                    title="Sin precio configurado"
                    ctaLabel="Añadir precio"
                    ctaAction={() => { setEditingSymbol(metal?.symbol); setEditValue(''); }}
                    compact
                  />
                )}
                {val && (
                  <div className="text-xs font-caption" style={{ color: 'var(--color-accent)' }}>ÍNDICE INTERNO</div>
                )}
                <div className="text-xs text-muted-foreground font-data">
                  {tick ? new Date(tick.ts)?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : ''}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
